<template>
	<div>
		<Flyout :title="titleView" :display="display" @show="close">
			<template #flyout-content>
				<div class="integrations-info">
					<div class="integrations-info__bod-type">
						<Dropdown
							label="BOD Type"
							name="bodtype"
							:autosuggestion="true"
							:displayHelp="false"
							:options="bodTypes"
							:required="true"
							v-model="dataSource.bodType"
							:errorText="bodTypeCustomErrorMessage"
							@change="setBodType"
						/>
					</div>
					<div class="integrations-info__bod-version">
						<Dropdown
							label="BOD Version"
							name="bodversion"
							:autosuggestion="true"
							:displayHelp="false"
							:options="bodVersions"
							:required="true"
							v-model="dataSource.bodVersion"
							:errorText="bodVersionErrorMessage"
							@change="setBodVersion"
						/>
					</div>
					<div class="integrations-info__active">
						<Toggle
							:label="dataSource.active ? 'Active' : 'Inactive'"
							v-model="dataSource.active"
							:disabled="isNew"
						/>
					</div>
				</div>
			</template>
			<template #flyout-footer>
				<ButtonGroup
					:items="[
						{
							btnClick: saveAction,
							className: 'btn--primary',
							labelText: isNew ? 'Create' : 'Update',
							disabled: disableSaveButton
						},
						{
							btnClick: close,
							className: 'btn btn--link',
							labelText: 'Cancel'
						}
					]"
				/>
			</template>
		</Flyout>
		<!-- <ConfirmationDialogue style="z-index:0" ref="confirmationDialogue" /> -->
	</div>
</template>

<script>
import ButtonGroup from '@/components/forms/button/button-group';
import Flyout from '@/components/shells/flyout/flyout';
import Toggle from '@/components/forms/selection-control/toggle/toggle';
import Dropdown from '@/components/forms/dropdown/dropdown.vue';
import ValidatePermissions from '@/sections/integration/validate-permission-mixins';
import { mapActions, mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import { isEmpty, deepEqual } from '@/utils/object';

//constants
import {
	MODULE_NAME,
	SET_SITEBOD,
	SAVE_SITEBOD,
	TOAST_MESSAGES_PUSH,
	VERSIONS,
	SET_VERSIONS
} from '@/sections/integration/integrations-mutation-types';

export default {
	name: 'SiteBodSettings',
	mixins: [ValidatePermissions],
	components: {
		ButtonGroup,
		Flyout,
		Toggle,
		Dropdown
	},
	model: {
		prop: 'display',
		event: 'show'
	},
	props: {
		siteId: {
			type: Number,
			require: true
		},
		display: {
			type: Boolean,
			require: true
		},
		siteBods: {
			type: Object,
			default: () => {}
		},
		source: {
			type: Object,
			default: () => {}
		}
	},
	validations: {
		dataSource: {
			bodType: {
				required
			},
			bodVersion: {
				required
			}
		}
	},
	data() {
		return {
			dataSource: {},
			fieldHasErrors: false,
			bodTypes: [],
			bodVersions: [],
			confirmartionTitle: 'SiteBod Settings'
		};
	},
	computed: {
		...mapGetters(MODULE_NAME, [VERSIONS]),
		isNew() {
			return !(this.source && this.source.id > 0);
		},
		titleView() {
			return `${this.isNew ? 'New' : 'Edit'} SiteBOD`;
		},
		disableSaveButton() {
			return (
				this.$v.$invalid || this.fieldHasErrors || !this.pendingChanges
			);
		},
		bodTypeCustomErrorMessage() {
			if (this.$v.dataSource.bodType.$dirty) {
				if (!this.$v.dataSource.bodType.required) {
					return 'Bod Type is required';
				} else {
					return '';
				}
			} else {
				return '';
			}
		},
		bodVersionErrorMessage() {
			if (this.$v.dataSource.bodVersion.$dirty) {
				if (!this.$v.dataSource.bodVersion.required) {
					return 'Bod Version is required';
				} else {
					return '';
				}
			} else {
				return '';
			}
		},
		pendingChanges() {
			return !deepEqual(this.dataSource, this.source || {});
		}
	},
	methods: {
		...mapActions(MODULE_NAME, [SET_VERSIONS]),
		async getVersions() {
			await this[SET_VERSIONS]();
		},
		async saveAction() {
			//validate before call api
			const { id, bodType, bodVersion } = this.dataSource;
			const matches = this.siteBods.items.find(
				x => x.bodType === bodType && x.bodVersion === bodVersion
			);
			if (matches) {
				//in the add action validate if it exists,
				//in the update action validate if there are duplicates
				if (this.isNew || (!this.isNew && matches.id !== id)) {
					this.$store.dispatch(TOAST_MESSAGES_PUSH, {
						statusCode: 400,
						message: `${bodType + bodVersion} already exists.`
					});
					this.fieldHasErrors = true;
					return;
				}
			}
			await this.$store.dispatch(
				`${MODULE_NAME}/${SAVE_SITEBOD}`,
				Object.assign(this.dataSource, {
					dcsIntegrationSiteIdsId: this.siteId
				})
			);
		},
		close() {
			this.$store.dispatch(`${MODULE_NAME}/${SET_SITEBOD}`, null);
		},
		setBodType(value) {
			this.dataSource.bodType = value;
			this.$v.dataSource.bodType.$touch();
		},
		setBodVersion(value) {
			this.dataSource.bodVersion = value;
			this.$v.dataSource.bodVersion.$touch();
		}
	},
	watch: {
		display: {
			immediate: true,
			handler(val) {
				if (val) {
					if (this.isAuthorized(this.source || {})) {
						this.dataSource = null;
						if (isEmpty(this[VERSIONS])) {
							this.getVersions();
						}
						this.dataSource = { ...this.source };
						if (this.isNew) {
							this.dataSource = {
								active: true,
								bodType: '',
								bodVersion: ''
							};
						}
					}
				}
			}
		},
		[VERSIONS]: {
			deep: true,
			immediate: true,
			handler(data) {
				const generateBodTypeVersion = (data, prop) => {
					const uniqueBODObj = {};
					const newArray = [];
					data.forEach(item => {
						const propertyValue = item[prop];
						if (propertyValue && !uniqueBODObj[propertyValue]) {
							uniqueBODObj[propertyValue] = true;
							const newObj = {
								text: propertyValue,
								value: propertyValue
							};
							newArray.push(newObj);
						}
					});
					return newArray;
				};

				if (data && data.items) {
					this.bodTypes = generateBodTypeVersion(
						data.items,
						'bodType'
					);
					this.bodVersions = generateBodTypeVersion(
						data.items,
						'bodVersion'
					);
				}
			}
		}
	}
};
</script>

<style lang="scss">
.integrations-info {
	> * {
		font-size: $flyout-content-font-size;
		min-width: 0;
	}

	display: grid;
	gap: $gutter-lg;
	margin-bottom: $gutter-lg;
}

/*------------------------------------*\
  #BASE MOBILE LANDSCAPE-UP
\*------------------------------------*/
@include mq($from: sm-l) {
	.integrations-info {
		grid-template-columns: 1fr 1fr;
	}
}

/*------------------------------------*\
  #BASE DESKTOP-UP
\*------------------------------------*/
@include mq($from: lg) {
	.integrations-info {
		grid-template-columns: 1fr 1fr 1fr 1fr;
	}
}
</style>
